import { useState } from "react";
import { useSelector } from "react-redux";
import useTranslator from "../../hooks/useTranslator";
import FreshFruitRunningTotals from "./components/FreshFruitRunningTotals";
import ColdRoomDataOverview from "./components/ColdRoomDataOverview";
import BiggestDaily from "./components/BiggestDaily";
import ProcessingOverview from "./components/ProcessingOverview";
import PackingOverview from "./components/PackingOverview";
import BrokerageOverview from "./components/BrokerageOverview";
import ReceivingAreaData from "./components/ReceivingAreaData";
import DashboardSection from "./components/DashboardSection";
import SeasonRangePicker from "./components/SeasonRangePicker";
import DateRangeFilter from "../../components/shared/fields/DateRangeFilter";
import "./components/dashboard-sections.css";
import { getCurrentDate, makePriorLotNumber } from "../../helpers/Utils";
import { jwtDecode } from "../../helpers/AccessControlUtils";

const Dashboard = () => {
  const { _t } = useTranslator();
  const [period, setPeriod] = useState("daily");

  // Overall Analytics date range — shared across all sub-sections
  const today = getCurrentDate("YYYY-MM-DD");
  const [analyticsRange, setAnalyticsRange] = useState({
    startDate: today,
    endDate: today,
  });

  const permissions = useSelector((state) => state.global.permissions);
  const decodedToken = jwtDecode();
  const userName = decodedToken?.name;
  const hasDashboardAccess = permissions.some(
    (perm) => Object.values(perm)[0] === "main.dashboard"
  );

  const isLoading = false;

  const periods = [
    { key: "daily",   label: _t("daily",   "Daily")   },
    { key: "weekly",  label: _t("weekly",  "Weekly")  },
    { key: "monthly", label: _t("monthly", "Monthly") },
  ];

  const periodToggle = (
    <ul className="db-period-toggle">
      {periods.map((p) => (
        <li
          key={p.key}
          className={period === p.key ? "active" : ""}
          onClick={() => setPeriod(p.key)}
        >
          {p.label}
        </li>
      ))}
    </ul>
  );

  if (!hasDashboardAccess) {
    return (
      <div className="db-welcome">
        <h2 className="db-welcome-title">
          {_t("welcome", "Welcome")}, <span className="db-welcome-name">{userName}</span> 👋
        </h2>
        <p className="db-welcome-sub">
          {_t("no_dashboard_access", "Welcome back! Let's keep your operations efficient and on track today.")}
        </p>
      </div>
    );
  }

  return (
    <div>
      <DashboardSection
        title={_t("fresh_fruit_running_totals_title", "Fresh Fruit Running Totals")}
        headerActions={<SeasonRangePicker />}
      >
        <FreshFruitRunningTotals />
      </DashboardSection>

      <DashboardSection
        title={_t("biggest_daily_title", "Biggest Daily")}
        headerActions={
          <span className="pf-live-badge">
            <span className="pf-live-dot" />
            LIVE TRACKING {`${today}`}
          </span>
        }
      >
        <BiggestDaily />
      </DashboardSection>

      {/* ── All date-filtered sections in one card ── */}
      <DashboardSection
        title={_t("overall_analytics", "Overall Analytics")}
        headerActions={
          <div className="db-analytics-header-actions">
            <div className="db-batch-code">
              <span className="db-batch-code-label">Targeted Batch Code</span>
              <span className="db-batch-code-value">
                {`#${makePriorLotNumber(analyticsRange.startDate)}`} - {`#${makePriorLotNumber(analyticsRange.endDate)}`}
              </span>
            </div>
            <DateRangeFilter
              triggerId="overall-analytics-date-range"
              onApplyRange={({ startDate, endDate }) =>
                setAnalyticsRange({ startDate, endDate })
              }
            />
          </div>
        }
      >
        <div className="db-daily-overview">
          <div className="db-daily-sub-title">{_t("receiving_area_data_title", "Receiving Overview")}</div>
          <ReceivingAreaData startDate={analyticsRange.startDate} endDate={analyticsRange.endDate} />

          <div className="db-daily-sub-title">{_t("cold_room_data_overview_title", "Cold + Room Data Overview")}</div>
          <ColdRoomDataOverview isLoading={isLoading} startDate={analyticsRange.startDate} endDate={analyticsRange.endDate} />

          <div className="db-daily-sub-title">{_t("processing_overview_title", "Processing Overview")}</div>
          <ProcessingOverview startDate={analyticsRange.startDate} endDate={analyticsRange.endDate} />

          <div className="db-daily-sub-title">{_t("packing_overview_title", "Packing Overview")}</div>
          <PackingOverview startDate={analyticsRange.startDate} endDate={analyticsRange.endDate} />

          <div className="db-daily-sub-title">{_t("brokerage_overview_title", "Brokerage Overview")}</div>
          <BrokerageOverview startDate={analyticsRange.startDate} endDate={analyticsRange.endDate} />
        </div>
      </DashboardSection>
    </div>
  );
};

export default Dashboard;
